/**
 * DNH — /subscribe/success
 *
 * Polls /api/subscription/status/{session_id} until the payment is confirmed
 * (or expired). Refreshes the auth context once premium is set, then redirects
 * the user to /account with a celebratory banner.
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Check, Loader2, AlertCircle } from "lucide-react";

import { SiteHeader, SiteFooter } from "../components/dnh/SiteChrome";
import { useAuth } from "../components/dnh/AuthContext";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_ATTEMPTS = 10;
const POLL_INTERVAL_MS = 2000;

export default function SubscribeSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { checkAuth, user, loading: authLoading, logout } = useAuth();
  const [mode, setMode] = useState(null);
  const [phase, setPhase] = useState("checking"); // checking | paid | failed | timeout
  const [tries, setTries] = useState(0);
  const returnPath = `/subscribe/success?session_id=${encodeURIComponent(sessionId || "")}`;
  const signInPath = `/signin?from=${encodeURIComponent(returnPath)}`;

  useEffect(() => {
    if(authLoading) return;
    if (!sessionId) {
      setPhase("failed");
      return;
    }
    if(!user) { setPhase("signin"); return; }
    setPhase("checking"); setTries(0);
    let cancelled = false;
    let timer;
    const controller = new AbortController();

    const poll = async (attempt) => {
      if (cancelled) return;
      try {
        const res = await fetch(
          `${BASE_URL}/api/subscription/status/${encodeURIComponent(sessionId)}`,
          { credentials: "include", signal: controller.signal }
        );
        const data = await res.json();
        if(cancelled)return;
        if(!res.ok) { setPhase(res.status === 401 ? "signin" : res.status === 403 ? "forbidden" : res.status === 404 ? "failed" : "timeout"); return; }
        setTries(attempt + 1);
        setMode(data.stripe_mode);
        if (["paid", "no_payment_required"].includes(data.payment_status) && data.fulfilled) {
          setPhase("paid");
          // Refresh auth context so /account & header reflect new tier
          await checkAuth();
          if(!cancelled && data.stripe_mode === "live") timer = setTimeout(() => navigate("/account?welcome=premium"), 2200);
          return;
        }
        if (["paid", "no_payment_required"].includes(data.payment_status) && data.subscription_status && !data.fulfilled) {
          setPhase("inactive"); return;
        }
        if (data.status === "expired") {
          setPhase("failed");
          return;
        }
        if (attempt >= MAX_ATTEMPTS - 1) {
          setPhase("timeout");
          return;
        }
        timer = setTimeout(() => poll(attempt + 1), POLL_INTERVAL_MS);
      } catch {
        if(cancelled)return;
        if (attempt >= MAX_ATTEMPTS - 1) setPhase("timeout");
        else timer = setTimeout(() => poll(attempt + 1), POLL_INTERVAL_MS);
      }
    };

    poll(0);
    return () => { cancelled = true; clearTimeout(timer); controller.abort(); };
  }, [sessionId, navigate, checkAuth, authLoading, user?.user_id]);

  return (
    <div className="min-h-screen bg-[var(--paper)] text-[var(--ink)] flex flex-col">
      <SiteHeader activeNav="Subscribe" />
      <main className="flex-1 mx-auto max-w-[640px] w-full px-4 sm:px-6 lg:px-10 py-20 text-center">
        {(phase === "signin" || phase === "forbidden") && <section data-testid="subscription-verification-signin">
          <h1 data-testid="subscription-verification-signin-title" className="font-serif text-4xl">Sign in to view your payment</h1>
          <p data-testid="subscription-verification-signin-message" className="text-sm mt-5 text-[var(--ink-2)]">Use the same account you used at checkout. You do not need to pay again.</p>
          {phase === "forbidden" ? <button data-testid="subscription-switch-account" onClick={async()=>{if(await logout()) navigate(signInPath);}} className="inline-block mt-8 border rounded-full px-5 py-3">Switch account</button> : <Link data-testid="subscription-verification-signin-link" to={signInPath} className="inline-block mt-8 border rounded-full px-5 py-3">Sign in to continue →</Link>}
        </section>}
        {phase === "inactive" && <section data-testid="subscription-verification-inactive"><h1 className="font-serif text-4xl">Payment recorded</h1><p className="text-sm mt-5">This checkout was completed, but its subscription is no longer active.</p><Link data-testid="subscription-inactive-account" to="/account?tab=settings" className="inline-block mt-8 underline">View account settings →</Link></section>}
        {phase === "checking" && (
          <div data-testid="subscribe-success-checking">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-full border border-[var(--hairline)] bg-[var(--surface-2)] mb-6">
              <Loader2 size={22} strokeWidth={1.5} className="text-[var(--ink-2)] animate-spin" />
            </div>
            <h1 className="font-serif text-[34px] leading-tight text-[var(--ink)]">
              Confirming your subscription…
            </h1>
            <p className="font-sans text-[14px] text-[var(--ink-2)] mt-3">
              Hang tight, this usually takes a few seconds.
            </p>
            <div className="font-mono text-[10px] text-[var(--muted)] mt-4 tracking-[0.14em] uppercase">
              Check {tries}/{MAX_ATTEMPTS}
            </div>
          </div>
        )}

        {phase === "paid" && (
          <div data-testid="subscribe-success-paid">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-[var(--crimson)] text-[var(--paper)] mb-6">
              <Check size={24} strokeWidth={2.25} />
            </div>
            <h1 className="font-serif text-[40px] leading-[1.05] text-[var(--ink)]">
              {mode === "test" ? "Test payment verified." : <>Welcome to <span className="italic text-[var(--crimson)]">Premium.</span></>}
            </h1>
            <p className="font-sans text-[15px] text-[var(--ink-2)] mt-3 max-w-md mx-auto">
              {mode === "test" ? "No real money moved. Your live subscription and administrator access were not changed." : "Your subscription is confirmed. Redirecting you to your account…"}
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 mt-6 text-[13.5px] font-medium text-[var(--ink)] dnh-link-underline"
              data-testid="subscribe-success-home-link"
            >
              Or jump straight to today's brief
              <ArrowRight size={14} strokeWidth={2} />
            </Link>
          </div>
        )}

        {(phase === "failed" || phase === "timeout") && (
          <div data-testid={`subscribe-success-${phase}`}>
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-full border border-[var(--hairline)] bg-[var(--surface-2)] mb-6">
              <AlertCircle size={22} strokeWidth={1.5} className="text-[var(--ink-2)]" />
            </div>
            <h1 className="font-serif text-[32px] leading-tight text-[var(--ink)]">
              {phase === "timeout"
                ? "Still confirming with Stripe…"
                : "Checkout could not be verified."}
            </h1>
            <p className="font-sans text-[14px] text-[var(--ink-2)] mt-3 max-w-md mx-auto">
              {phase === "timeout"
                ? "Your payment may still complete — check your email or refresh your account page in a moment."
                : "We could not verify this checkout. Check your account or contact support before trying another payment."}
            </p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <Link
                data-testid="subscription-result-retry"
                to="/subscribe"
                className="inline-flex items-center gap-1.5 h-10 px-4 rounded-[8px] bg-[var(--ink)] text-[var(--paper)] hover:bg-[var(--crimson)] font-medium text-[13px]"
              >
                Try again
                <ArrowRight size={13} strokeWidth={2} />
              </Link>
              <Link
                data-testid="subscription-result-account"
                to="/account"
                className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-[var(--ink-2)] hover:text-[var(--crimson)] dnh-link-underline"
              >
                Go to account
              </Link>
            </div>
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
